import { EditorContent, useEditor } from '@tiptap/react';
import clsx from 'clsx';
import { Bold, Italic, List, ListOrdered, Underline as UnderlineIcon } from 'lucide-react';
import { memo, useEffect, useState } from 'react';
import { useDebouncedCallback } from 'use-debounce';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardFooter } from '@/components/ui/card';
import { toast } from '@/components/ui/use-toast';
import type {
  GenMeetingNotesPayload,
  MeetingNotesObject,
  MeetingType,
} from '@/features/ai-notes/schemas';
import {
  createSimplifiedPayload,
  preprocessMeetingNotesData,
} from '@/features/ai-notes/utils/data-preprocessor';
import { isMondayMeeting } from '@/features/ai-notes/utils/key-metrics-calculator';
import { stripGeneratedSections } from '@/features/ai-notes/utils/strip-generated-sections';
import { BtnGenerateNotes } from '@/features/notes/components/btn-generate-notes';
import { BtnInsertNotepad } from '@/features/notes/components/btn-insert-notepad';
import { BtnInsertNotes } from '@/features/notes/components/btn-insert-notes';
import { useGenerateNotes } from '@/features/notes/hooks/use-generate-notes';
import { type UseMeetingNotesProps, useMeetingNotes } from '@/features/notes/hooks/use-notes-new';
import type { ReportDataType } from '@/features/reports/context/report-data-context';
import { useNewGroupKeysQuery } from '@/features/reports/gql/_gen_/group.gql';
import { useIsShared } from '@/features/reports/hooks/use-report-location';
import type { Meeting } from '@/graphql/types';
import { betweenOrEqual, DateHelpers } from '@/helpers/date-helpers';
import {
  extensions,
  handlePaste,
  loadFromLocalStorage,
  parseContent,
  saveToLocalStorage,
} from '@/lib/tiptap';

const AUTOSAVE_DELAY = 10000;

const convertAiContentToTiptapJson = (content: MeetingNotesObject) => {
  const doc: any = {
    type: 'doc',
    content: [],
  };

  // Helper to create a text node only if the text is non-empty
  const safeTextNode = (text: any) => {
    if (typeof text === 'string' && text.trim().length > 0) {
      return { type: 'text', text };
    }
    return null;
  };

  // Helper to create a heading only if the title is non-empty
  const safeHeading = (level: number, title: any) => {
    const node = safeTextNode(title);
    if (node) {
      return {
        type: 'heading',
        attrs: { level },
        content: [node],
      };
    }
    return null;
  };

  // Helper to create a paragraph only if the text is non-empty
  const safeParagraph = (text: any) => {
    const node = safeTextNode(text);
    if (node) {
      return {
        type: 'paragraph',
        content: [node],
      };
    }
    return null;
  };

  // lastNight: only include if summary has content
  if (
    content.lastNight?.summary &&
    typeof content.lastNight.summary === 'string' &&
    content.lastNight.summary.trim().length > 0
  ) {
    const lastNightHeading = safeHeading(4, content.lastNight.title);
    if (lastNightHeading) doc.content.push(lastNightHeading);
    const summaryParagraph = safeParagraph(content.lastNight.summary);
    if (summaryParagraph) doc.content.push(summaryParagraph);
  }

  // rateShops: only include if summaryItems has content
  if (
    content.rateShops?.summaryItems &&
    Array.isArray(content.rateShops.summaryItems) &&
    content.rateShops.summaryItems.length > 0
  ) {
    const rateShopsHeading = safeHeading(4, content.rateShops.title);
    if (rateShopsHeading) doc.content.push(rateShopsHeading);
    const bulletList = {
      type: 'bulletList',
      content: content.rateShops.summaryItems
        .map((item) => {
          const para = safeParagraph(item);
          if (!para) return null;
          return {
            type: 'listItem',
            content: [para],
          };
        })
        .filter(Boolean),
    };
    if (bulletList.content.length > 0) doc.content.push(bulletList);
  }

  // rmRecs: only include if summary has content
  if (
    content.rmRecs?.summary &&
    typeof content.rmRecs.summary === 'string' &&
    content.rmRecs.summary.trim().length > 0
  ) {
    const rmRecsHeading = safeHeading(4, content.rmRecs.title);
    if (rmRecsHeading) doc.content.push(rmRecsHeading);
    const rmRecsSummary = safeParagraph(content.rmRecs.summary);
    if (rmRecsSummary) doc.content.push(rmRecsSummary);
  }

  // Only show Key Metrics section if there are actual alerts
  const hasOooAlert = content.keyMetrics?.metrics?.ooo?.hasAlert;
  const hasDelta1DayAlert = content.keyMetrics?.metrics?.delta_1day?.hasAlert;

  if (content.keyMetrics && (hasOooAlert || hasDelta1DayAlert)) {
    const keyMetricsHeading = safeHeading(4, content.keyMetrics.title);
    if (keyMetricsHeading) doc.content.push(keyMetricsHeading);

    // Add OOO alert as single paragraph
    if (hasOooAlert && content.keyMetrics?.metrics?.ooo?.alertMessage) {
      const oooAlert = safeParagraph(content.keyMetrics.metrics.ooo.alertMessage);
      if (oooAlert) doc.content.push(oooAlert);
    }

    // Add delta alert as single paragraph
    if (hasDelta1DayAlert && content.keyMetrics?.metrics?.delta_1day?.alertMessage) {
      const deltaAlert = safeParagraph(content.keyMetrics.metrics.delta_1day.alertMessage);
      if (deltaAlert) doc.content.push(deltaAlert);
    }
  }

  if (
    content.groups &&
    Array.isArray(content.groups.summaryItems) &&
    content.groups.summaryItems.length > 0
  ) {
    const groupsHeading = safeHeading(4, content.groups.title);
    if (groupsHeading) doc.content.push(groupsHeading);
    const groupsSummary = safeParagraph(content.groups.summary);
    if (groupsSummary) doc.content.push(groupsSummary);
    const bulletList = {
      type: 'bulletList',
      content: content.groups.summaryItems
        .map((item) => {
          const para = safeParagraph(item);
          if (!para) return null;
          return {
            type: 'listItem',
            content: [para],
          };
        })
        .filter(Boolean),
    };
    if (bulletList.content.length > 0) doc.content.push(bulletList);
  }

  if (
    content.newGroups &&
    Array.isArray(content.newGroups.summaryItems) &&
    content.newGroups.summaryItems.length > 0
  ) {
    const newGroupsHeading = safeHeading(4, content.newGroups.title);
    if (newGroupsHeading) doc.content.push(newGroupsHeading);
    const bulletList = {
      type: 'bulletList',
      content: content.newGroups.summaryItems
        .map((item) => {
          const para = safeParagraph(item);
          if (!para) return null;
          return {
            type: 'listItem',
            content: [para],
          };
        })
        .filter(Boolean),
    };
    if (bulletList.content.length > 0) doc.content.push(bulletList);
  }

  if (
    content.additionalNotes &&
    Array.isArray(content.additionalNotes.notes) &&
    content.additionalNotes.notes.length > 0
  ) {
    const additionalNotesHeading = safeHeading(4, content.additionalNotes.title);
    if (additionalNotesHeading) doc.content.push(additionalNotesHeading);
    const bulletList = {
      type: 'bulletList',
      content: content.additionalNotes.notes
        .map((item) => {
          const para = safeParagraph(item);
          if (!para) return null;
          return {
            type: 'listItem',
            content: [para],
          };
        })
        .filter(Boolean),
    };
    if (bulletList.content.length > 0) doc.content.push(bulletList);
  }

  return doc;
};

interface MeetingNotesProps extends UseMeetingNotesProps {
  parentName: string;
  reportData?: ReportDataType;
  loading?: boolean;
  setMeeting?: (meeting: Meeting) => void;
}

const MeetingNotes = memo(function MeetingNotes(props: MeetingNotesProps) {
  const { connection, data: aiNotes, error, status, setStatus } = useGenerateNotes();
  const [isMounted, setIsMounted] = useState(true);
  const { isShared } = useIsShared();
  const { parentName, reportData, loading, meeting, ...rest } = props;
  const [isSaving, setIsSaving] = useState(false);
  const { handleSave, notes, notepad, prevNotes } = useMeetingNotes({
    meeting,
    ...rest,
  });

  // Fetch new group keys for AI notes generation
  // Use meeting date if available, otherwise use today
  const effectiveMeetingDate = meeting?.meetingDate || DateHelpers.today();

  const { data: newGroupKeysData } = useNewGroupKeysQuery({
    variables: {
      brandCode: rest.brandCode || '',
      meetingDate: effectiveMeetingDate,
    },
    skip: !rest.brandCode,
  });

  // Get from rmData the values for ooo and the sold-rooms delta for the next five days.
  // On Monday meetings the previous snapshot the team saw was Friday's, so Δ1D would
  // hide weekend bookings for every stay date — use Δ3D instead. See isMondayMeeting.
  const rmData = reportData?.dataReport?.rmData;
  const useThreeDayDelta = isMondayMeeting(effectiveMeetingDate);
  const nextFiveDays = rmData?.filter((r) =>
    betweenOrEqual(r?.stay_date, DateHelpers.today(), DateHelpers.fromToday(5)),
  );
  const keyMetrics = nextFiveDays?.map((f) => ({
    stay_date: f?.stay_date,
    capacity: f?.total_capacity ?? 0,
    ooo: f?.ooo ?? 0,
    delta_1day: (useThreeDayDelta ? f?.delta_3day : f?.delta_1day) ?? 0,
  }));

  const triggerAiGeneration = () => {
    if (connection) {
      // Capture the user's own notes from the editor. Previously generated sections are
      // stripped — feeding them back in makes the LLM file them under "Additional Notes",
      // which compounds on every regeneration.
      const existingContent =
        editor && !editor.isEmpty ? stripGeneratedSections(editor.getJSON()) : '';

      // Preprocess all data - calculations and formatting done here
      // Pass meeting type to prioritize appropriate rate changes (1-day for DAILY, 7-day for WEEKLY)
      const meetingCategory = meeting?.category || 'DAILY';
      const processedData = preprocessMeetingNotesData(
        {
          keyMetrics,
          fiveDayRatesData: reportData?.fiveDayRatesData,
          rmRecsData: reportData?.dataRmRecs,
          groupData: reportData?.dataReport?.groupDaily,
          newGroupKeys: (newGroupKeysData?.newGroupKeys || []).filter(Boolean) as string[],
          existingNotes: existingContent,
          brandCode: rest.brandCode,
          userId: rest.userId,
          meetingDate: effectiveMeetingDate,
        },
        meetingCategory,
      );

      // Create simplified payload with pre-calculated sections
      const simplifiedPayload = createSimplifiedPayload(processedData, meetingCategory);

      const payload: GenMeetingNotesPayload = {
        action: 'genMeetingNotes',
        type: (meeting?.category || 'DAILY') as MeetingType,
        data: {
          brandCode: rest.brandCode,
          userId: rest.userId,
          processedSections: simplifiedPayload.sections,
          existingNotes: existingContent,
        },
      };
      connection.send(JSON.stringify(payload));
      setStatus('Thinking...');
    } else {
      alert('Hello from Skynet!');
    }
  };

  const flexWidth = parentName === 'meeting-tools' ? 'max-w-[400px]' : 'w-full';

  const editor = useEditor({
    extensions,
    content: notes,
    editable: !isShared,
    autofocus: true,
    editorProps: {
      attributes: {
        class: `outline-none ${flexWidth} min-h-[5px] p-4 leading-extra-tight text-sm [&_li>p]:my-2.5 [&_h3]:mt-0`,
      },
      handlePaste,
    },
  });

  useEffect(() => {
    setIsMounted(true);
    return () => {
      setIsMounted(false);
      if (editor) {
        editor.destroy();
      }
    };
  }, [editor]);

  useEffect(() => {
    if (aiNotes) {
      const { role, content, type } = aiNotes;
      if (role === 'assistant' && type === 'object' && content) {
        if (editor && content) {
          const tiptapJson = convertAiContentToTiptapJson(content);
          editor.commands.setContent(tiptapJson);
        }
      }
    }
  }, [aiNotes, editor]);

  const handleInsert = (source: 'notepad' | 'prevNotes' | 'aiNotes') => {
    const contentToInsert =
      source === 'notepad' ? notepad : source === 'prevNotes' ? prevNotes : (aiNotes as any);
    const jsonToInsert = parseContent(contentToInsert);

    if (editor) {
      if (editor.isEmpty) {
        editor.commands.setContent(jsonToInsert);
      } else {
        editor.commands.insertContent(jsonToInsert);
        // Insert at cursor position
        // editor.chain().focus().insertContent(jsonToInsert.content).run();
      }
    }
  };

  const saveNotes = async (content: object) => {
    if (!isMounted) return;
    setIsSaving(true);
    try {
      await handleSave(JSON.stringify(content));
      saveToLocalStorage({ brandCode: rest.brandCode, content });
      if (isMounted) {
        toast({
          title: 'Notes saved',
          description: 'Your meeting notes have been saved successfully.',
        });
      }
    } catch (error) {
      console.error('Failed to save notes:', error);
      if (isMounted) {
        toast({
          title: 'Error saving notes',
          description: "There was a problem saving your notes. They've been stored locally.",
          variant: 'destructive',
        });
      }
    } finally {
      if (isMounted) setIsSaving(false);
    }
  };

  const debouncedSave = useDebouncedCallback(async (content: object) => {
    await saveNotes(content);
  }, AUTOSAVE_DELAY);

  useEffect(() => {
    return () => {
      debouncedSave.cancel();
    };
  }, [debouncedSave]);

  useEffect(() => {
    if (editor && props.brandCode) {
      const savedContent = loadFromLocalStorage({ brandCode: rest.brandCode });
      const contentToSet = parseContent(notes || savedContent);

      if (editor.isEmpty || JSON.stringify(editor.getJSON()) !== JSON.stringify(contentToSet)) {
        editor.commands.setContent(contentToSet || null);
      }
    }
  }, [editor, notes, props.brandCode]);

  useEffect(() => {
    const handleBeforeUnload = (e: BeforeUnloadEvent) => {
      if (
        editor &&
        !editor.isEmpty &&
        JSON.stringify(editor.getJSON()) !== JSON.stringify(parseContent(notes))
      ) {
        e.preventDefault();
        e.returnValue = '';
      }
    };

    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, [editor, notes]);

  useEffect(() => {
    if (editor) {
      editor.on('update', ({ editor }) => {
        const content = editor.getJSON();
        debouncedSave(content);
      });
    }
  }, [editor, debouncedSave]);

  const handleManualSave = async () => {
    if (editor) {
      const content = editor.getJSON();
      await saveNotes(content);
    }
  };

  return (
    <Card className='w-full mx-auto'>
      <CardContent className='p-2'>
        <div className='p-2'>
          {!isShared && (
            <div className='flex gap-2 flex-wrap'>
              <Button
                variant='outline'
                size='icon-sm'
                onClick={() => editor?.chain().focus().toggleBold().run()}
                className={editor?.isActive('bold') ? 'bg-muted' : ''}
              >
                <Bold className='h-3 w-3' />
              </Button>
              <Button
                variant='outline'
                size='icon-sm'
                onClick={() => editor?.chain().focus().toggleItalic().run()}
                className={editor?.isActive('italic') ? 'bg-muted' : ''}
              >
                <Italic className='h-3 w-3' />
              </Button>
              <Button
                variant='outline'
                size='icon-sm'
                onClick={() => editor?.chain().focus().toggleUnderline().run()}
                className={editor?.isActive('underline') ? 'bg-muted' : ''}
              >
                <UnderlineIcon className='h-3 w-3' />
              </Button>
              <Button
                variant='outline'
                size='icon-sm'
                onClick={() => editor?.chain().focus().toggleBulletList().run()}
                className={editor?.isActive('bulletList') ? 'bg-muted' : ''}
              >
                <List className='h-3 w-3' />
              </Button>
              <Button
                variant='outline'
                size='icon-sm'
                onClick={() => editor?.chain().focus().toggleOrderedList().run()}
                className={editor?.isActive('orderedList') ? 'bg-muted' : ''}
              >
                <ListOrdered className='h-3 w-3' />
              </Button>
              <BtnInsertNotes handleInsert={() => handleInsert('prevNotes')} />
              <BtnInsertNotepad handleInsert={() => handleInsert('notepad')} />
              <BtnGenerateNotes
                connection={connection}
                dataReady={!loading}
                triggerGeneration={triggerAiGeneration}
                parentName={parentName}
              />
              {status && <span className='text-sm animate-pulse'>{status}</span>}
            </div>
          )}
        </div>
        <div className='w-full border rounded-sm mx-1'>
          <EditorContent editor={editor} className='prose prose-sm max-w-none w-full' />
        </div>
        <div className='flex justify-start px-1 py-1'>
          <Button
            className={clsx(isShared && 'hidden')}
            onClick={handleManualSave}
            disabled={isSaving || isShared}
          >
            {isSaving ? 'Saving...' : 'Save'}
          </Button>
        </div>
      </CardContent>
      {error && <CardFooter>{error?.message}</CardFooter>}
    </Card>
  );
});

export default MeetingNotes;
